/**
 * @author Ahmed Samer
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2019-12-09
 * @description Implementation of company details react component
 * @filename company-controller.tsx
 */

import { Entity as GraphQlEntity, GRAPHQL_TYPE_ENTITY, Entity } from "@radivision/graphql/lib/ts/graphql/entity";
import React from "react";
import { connect } from "react-redux";
import { RelayProps } from "../../../relay/relay-props";
import { Loader } from "../../page/loader";
import { ProfileHeader } from "../profile-header/profile-header";
import { CompanyDetails, CompanyDetailsProps } from "./company-details";
import { SideBar } from "./sidebar";
import * as Actions from "../../../redux/actions";
import { ENVIRONMENT } from "../../../relay/relay-environment";
import { FetchEntityPayload, FetchEntityAction } from "../../../redux/cache/types";
import { ConnectInvestorModal } from "../profile-details-tab/investment/connect-investor-modal";
import { isOwner } from "../../../utilities/is-owner";
import { AppState, store } from "../../../redux";
import { Person, PreviewKind, TopLevelMediaKind } from "@radivision/graphql";
import { cacheData } from "../../../redux/actions";
import { ImageHelper } from "../../../utilities/image-helper";
import { getProfileInfo } from "../../../utilities/get-profile-info";
import { ProfileMetaTags } from "../../seo/profile-meta";
import { buildCompanyMeta, ProfileMeta } from "../../../utilities/profile-meta";
import { PAGE_TITLE_CONTEXT } from "../../../contexts/page-title-context";
interface CompanyControllerProps extends RelayProps {
  list?: GraphQlEntity;
  error: any;
  user: Person | null;
}

interface CompanyControllerState {
  owner: boolean;
  entity: GraphQlEntity;
}

class CompanyController extends React.Component<CompanyControllerProps, CompanyControllerState> {
  static contextType = PAGE_TITLE_CONTEXT;
  context!: React.ContextType<typeof PAGE_TITLE_CONTEXT>;

  private reportedTitle: string | null = null;
  private reportTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: CompanyControllerProps) {
    super(props);
  }

  componentDidMount() {
    // App assigns pendingPageViewKey *after* the synchronous setState that mounts this
    // component, so reporting inline here would land before there is a page view to
    // attach the title to and then be suppressed by the dedupe below. Defer a tick so the
    // title arrives once App is ready for it - this matches the passive-effect timing the
    // person controller gets for free.
    this.reportTimer = setTimeout(() => {
      this.reportTimer = null;
      this.reportPageTitle();
    }, 0);
  }

  componentDidUpdate() {
    this.reportPageTitle();
  }

  componentWillUnmount() {
    if (this.reportTimer !== null) {
      clearTimeout(this.reportTimer);
      this.reportTimer = null;
    }
  }

  /**
   * entity is undefined until its fetch resolves, so the title is only reported once
   * there is a company to name.
   */
  private reportPageTitle(): void {
    const meta = this.buildMeta();
    if (meta && meta.title !== this.reportedTitle) {
      this.reportedTitle = meta.title;
      this.context.setPageTitle(meta.title);
    }
  }

  private buildMeta(): ProfileMeta | null {
    const entity = this.props.list;
    return entity?.name ? buildCompanyMeta(entity.name, entity.industry, entity.tagLine) : null;
  }

  render(): React.ReactNode {
    const { relay, user, list: entity, error } = this.props;
    const owner = isOwner(entity, user);
    const props = {
      list: entity,
      relay,
      owner,
      updateContext: this.updateComponentContext,
    };
    const previewUrl = entity?.previews
      ? ImageHelper.fetchOptimizedImageUrl({
          imageType: "PREVIEW",
          preview: {
            content: entity.previews,
            previewKind: PreviewKind.LOGO,
            topLevelMedia: TopLevelMediaKind.IMAGE,
          },
          desiredDimensions: { containerWidthRatio: 3 / 4, numberOfItems: 1 },
          isSquareImage: false,
          revision: entity.revision,
        })
      : null;
    const { profileUrl } = getProfileInfo(entity);
    const meta = this.buildMeta();
    return (
      <div>
        {meta ? (
          <ProfileMetaTags meta={meta} canonicalPath={profileUrl} imageUrl={previewUrl?.requestedResolutionUrl} />
        ) : null}
        <ProfileHeader
          type={GRAPHQL_TYPE_ENTITY}
          value={entity}
          environment={ENVIRONMENT}
          owner={owner}
          updateContext={this.updateComponentContext}
        />
        {error && (
          <div
            className="alert alert-danger about-danger"
            style={{ transform: "translateY(20px)", justifyContent: "center" }}
          >
            Error Loading Company Profile!
          </div>
        )}
        <div className="profile-body">
          <SideBar {...props} />
          <CompanyDetails {...props} />
        </div>
        <ConnectInvestorModal />
      </div>
    );
  }

  updateComponentContext(entity: GraphQlEntity): void {
    if (entity) {
      store.dispatch(cacheData({ key: entity.id, data: entity }));
    }
  }
}

interface MapDispatchProps {
  fetchEntity: (payload: FetchEntityPayload) => FetchEntityAction;
}

interface MapStateProps {
  entity: Entity;
  error: any;
  isFetching: boolean;
}

type Props = CompanyControllerProps & MapStateProps & MapDispatchProps;
export class CompanyFetcher extends React.Component<Props, CompanyControllerState> {
  constructor(props: Props) {
    super(props);
    this.fetchEntity = this.fetchEntity.bind(this);
  }

  componentDidMount() {
    this.fetchEntity();
  }

  fetchEntity() {
    const { entity, isFetching, list, fetchEntity } = this.props;
    const id = list?.id;
    if (id && !entity && !isFetching) {
      fetchEntity({ id });
    }
  }

  render() {
    const { entity, error, user } = this.props;
    return <CompanyController user={user} list={entity} relay={{ environment: ENVIRONMENT }} error={error} />;
  }
}

const mapState = (state: AppState, props: CompanyControllerProps) => ({
  user: state.account.user,
  entity: state.cache.store && props.list?.id && state.cache.store[props.list?.id],
  error: state.cache.errors && props.list?.id && state.cache.errors[props.list?.id],
  isFetching: props.list?.id ? state.cache.inProgress[props.list?.id] : false,
});

const mapDispatch = {
  fetchEntity: Actions.fetchEntity,
};

export const CompanyControllerContainer = connect(mapState, mapDispatch)(CompanyFetcher);
